import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha, hoyCaracas } from '../utils/format';
import { pref } from '../utils/moneda';
import { claseMonto } from '../components/contabilidad/ReporteFinanciero';
import SelectorMoneda, { useVistaMoneda } from '../components/reportes/SelectorMoneda';
import Exportar from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import BotonAyuda from '../components/BotonAyuda';
import EtapasCierre from '../components/cierre/EtapasCierre';
import CierreIA from '../components/cierre/CierreIA';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

// ============================================================
// Cierre mensual o anual (#121): el paquete de cierre de la empresa activa
// en una página — revisión previa, cuentas, resultados, balance y lo que
// queda por cobrar y pagar — y cada bloque lleva a su reporte completo con
// el mismo período. En un mes, arriba van las etapas del cierre (#111,
// EtapasCierre): revisar la lista y cerrar el mes con bloqueo; debajo, la
// IA del cierre (CierreIA): análisis del mes y revisión por movimiento.
// ============================================================

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const ESTRUCTURA = [
  { seccion: 'ingreso_operativo' },
  { seccion: 'costo' },
  { total: 'utilidad_bruta', label: 'Utilidad bruta' },
  { seccion: 'gasto' },
  { total: 'utilidad_operativa', label: 'Utilidad operativa' },
  { seccion: 'otro_ingreso' },
  { seccion: 'otro_gasto' },
  { seccion: 'sin_clasificar' },
  { total: 'utilidad_neta', label: 'Utilidad neta', final: true }
];

function mesAnterior() {
  const [a, m] = hoyCaracas().slice(0, 7).split('-').map(Number);
  return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, '0')}`;
}

const nombrePeriodo = (d) => (d.tipo === 'anio' ? `Año ${d.valor}` : `${MESES[Number(d.valor.slice(5, 7)) - 1]} ${d.valor.slice(0, 4)}`);

function Bloque({ titulo, enlace, textoEnlace, children }) {
  return (
    <section className="bg-white rounded-xl shadow-sm overflow-hidden break-inside-avoid">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
        <h2 className="text-base font-semibold text-slate-700">{titulo}</h2>
        {enlace && (
          <Link to={enlace} className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 print:hidden">
            {textoEnlace} <ArrowRight size={14} />
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

export default function CierrePeriodo() {
  const { empresaActiva, empresaActivaNombre, rolActual } = useAuth();
  const [vista, setVista] = useVistaMoneda();
  const [tipo, setTipo] = useState('mes');
  const [mes, setMes] = useState(mesAnterior);
  const [anio, setAnio] = useState(() => hoyCaracas().slice(0, 4));
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [abiertas, setAbiertas] = useState({});

  const valor = tipo === 'anio' ? anio : mes;

  const cargar = () => api.get('/reportes/cierre', { params: { tipo, valor, vista } })
    .then(({ data }) => setDatos(data))
    .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar el cierre'));

  useEffect(() => {
    if (!empresaActiva || !valor) return;
    setDatos(null);
    setError('');
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva, tipo, valor, vista]);

  const pv = pref(vista);
  const m = (v) => `${Number(v) < 0 ? '-' : ''}${pv}${formatMonto(Math.abs(Number(v) || 0))}`;
  const mc = (v, moneda) => `${Number(v) < 0 ? '-' : ''}${pref(moneda)}${formatMonto(Math.abs(Number(v) || 0))}`;

  const anios = Array.from({ length: 6 }, (_, i) => String(Number(hoyCaracas().slice(0, 4)) - i));

  const contenidoExcel = () => {
    const d = datos;
    const sec = Object.fromEntries(d.resultados.secciones.map((s) => [s.key, s]));
    return {
      titulo: `Cierre ${nombrePeriodo(d)}`,
      detalle: `Del ${formatFecha(d.desde)} al ${formatFecha(d.hasta)} · resultados y balance en ${vista}, cuentas en su moneda`,
      hojas: [
        {
          nombre: 'Cuentas',
          columnas: [
            { titulo: 'Cuenta', tipo: 'texto' }, { titulo: 'Moneda', tipo: 'texto' }, { titulo: 'Saldo inicial', tipo: 'monto' },
            { titulo: 'Ingresos', tipo: 'monto' }, { titulo: 'Egresos', tipo: 'monto' }, { titulo: 'Transferencias', tipo: 'monto' },
            { titulo: 'Saldo final', tipo: 'monto' }, { titulo: 'Sin conciliar', tipo: 'entero' }
          ],
          filas: [
            ...d.cuentas.map((c) => [c.nombre, c.moneda, c.saldo_inicial, c.ingresos, c.egresos, c.transferencias, c.saldo_final, c.sin_conciliar]),
            ...d.totales_cuentas.map((t) => ({ valores: [`Total ${t.moneda}`, t.moneda, t.saldo_inicial, t.ingresos, t.egresos, t.transferencias, t.saldo_final, ''], negrita: true }))
          ]
        },
        {
          nombre: 'Resultados',
          columnas: [{ titulo: 'Partida', tipo: 'texto' }, { titulo: 'Ingreso', tipo: 'monto' }, { titulo: 'Egreso', tipo: 'monto' }, { titulo: `Neto (${vista})`, tipo: 'monto' }],
          filas: ESTRUCTURA.flatMap((e) => {
            if (e.total) return [{ valores: [e.label, d.resultados[e.total].ingreso, d.resultados[e.total].egreso, d.resultados[e.total].total], negrita: true }];
            const s = sec[e.seccion];
            if (!s || !s.lineas.length) return [];
            return [
              { valores: [s.label, s.ingreso, s.egreso, s.total], negrita: true },
              ...s.lineas.map((l) => [`   ${l.grupo_nombre !== l.nombre ? `${l.grupo_nombre} · ` : ''}${l.nombre}`, l.ingreso, l.egreso, l.total])
            ];
          })
        },
        {
          nombre: 'Balance',
          columnas: [{ titulo: 'Partida', tipo: 'texto' }, { titulo: `Monto (${vista})`, tipo: 'monto' }],
          filas: [
            ...[['ACTIVO', d.balance.activo, d.balance.total_activo], ['PASIVO', d.balance.pasivo, d.balance.total_pasivo], ['PATRIMONIO', d.balance.patrimonio, d.balance.total_patrimonio]]
              .flatMap(([label, bloques, total]) => [
                ...bloques.flatMap((b) => [{ valores: [b.label, b.total], negrita: true }, ...b.lineas.map((l) => [`   ${l.nombre}`, l.monto])]),
                { valores: [`Total ${label.toLowerCase()}`, total], negrita: true }
              ])
          ]
        }
      ]
    };
  };

  const revision = datos && [
    datos.revision.sin_conciliar > 0 && { texto: `${datos.revision.sin_conciliar} movimientos sin conciliar`, ir: '/conciliacion' },
    datos.revision.sin_categoria > 0 && { texto: `${datos.revision.sin_categoria} movimientos sin categoría`, ir: '/movimientos' },
    datos.revision.sin_clasificar > 0 && { texto: `${datos.revision.sin_clasificar} movimientos en categorías sin clasificar en el estado de resultados`, ir: '/contabilidad/configuracion' },
    datos.revision.sin_tasa > 0 && { texto: `${datos.revision.sin_tasa} movimientos en Bs sin tasa del día`, ir: '/tasas-cambio' },
    datos.revision.sin_cierre_inventario && { texto: 'No hay cierre de inventario a esta fecha (el balance sale sin inventario)', ir: '/inventario/cierre' }
  ].filter(Boolean);

  const cxc = datos?.balance.activo.find((b) => b.key === 'cxc');
  const cxp = datos?.balance.pasivo.find((b) => b.key === 'cxp');
  const sec = datos ? Object.fromEntries(datos.resultados.secciones.map((s) => [s.key, s])) : {};

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            Cierre del período
            {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
            <BotonAyuda />
          </h1>
          <p className="text-sm text-slate-500 mt-1">Todo el período en una página; cada bloque abre su reporte completo con las mismas fechas.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SelectorMoneda vista={vista} onChange={setVista} />
          <Exportar excel={contenidoExcel} disabled={!datos} />
        </div>
      </div>

      {datos && (
        <EncabezadoImpresion
          titulo={`Cierre ${nombrePeriodo(datos)}`}
          detalle={`Del ${formatFecha(datos.desde)} al ${formatFecha(datos.hasta)} · resultados y balance en ${vista}`}
        />
      )}

      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-end gap-3 text-sm print:hidden">
        <div className="inline-flex rounded-md border overflow-hidden">
          {[['mes', 'Mes'], ['anio', 'Año']].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTipo(k)}
              className={`px-3 py-2 ${tipo === k ? 'bg-primary-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              {label}
            </button>
          ))}
        </div>
        {tipo === 'mes' ? (
          <input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="border rounded-md px-3 py-2" />
        ) : (
          <select value={anio} onChange={(e) => setAnio(e.target.value)} className="border rounded-md px-3 py-2">
            {anios.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        {datos && <span className="text-slate-400">Del {formatFecha(datos.desde)} al {formatFecha(datos.hasta)}</span>}
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}
      {!datos && !error && <p className="text-slate-400 text-sm">Cargando…</p>}

      {datos && (
        <>
          {datos.en_curso && (
            <p className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertTriangle size={16} /> Este período todavía no termina: las cifras pueden cambiar.
            </p>
          )}

          {/* Cierre del mes por etapas (#111); en el año, solo la revisión */}
          {datos.cierre ? (
            <>
              <EtapasCierre cierre={datos.cierre} enCurso={datos.en_curso} rolActual={rolActual} onCambio={cargar} />
              <CierreIA mes={datos.cierre.mes} estadoMes={datos.cierre.estado} enCurso={datos.en_curso} rolActual={rolActual} onCambio={cargar} />
            </>
          ) : (
          <Bloque titulo="Antes de cerrar">
            {revision.length === 0 ? (
              <p className="flex items-center gap-2 px-4 py-3 text-sm text-slate-600">
                <CheckCircle2 size={16} className="text-green-500" /> Nada pendiente: todo conciliado, categorizado y con tasa.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {revision.map((r) => (
                  <li key={r.texto}>
                    <Link to={r.ir} className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                      <AlertTriangle size={15} className="text-amber-500 shrink-0" />
                      <span className="flex-1">{r.texto}</span>
                      <ChevronRight size={15} className="text-slate-300 print:hidden" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Bloque>
          )}

          {/* Cuentas */}
          <Bloque
            titulo="Bancos y cajas"
            enlace={`/reportes/flujo-caja?desde=${datos.desde.slice(0, 7)}&hasta=${datos.hasta.slice(0, 7)}`}
            textoEnlace="Ver flujo de caja"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="p-3">Cuenta</th>
                    <th className="p-3 text-right whitespace-nowrap">Saldo inicial</th>
                    <th className="p-3 text-right">Ingresos</th>
                    <th className="p-3 text-right">Egresos</th>
                    <th className="p-3 text-right">Transferencias</th>
                    <th className="p-3 text-right whitespace-nowrap">Saldo final</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.cuentas.map((c) => (
                    <tr key={c.id} className="border-t">
                      <td className="p-3">
                        {c.nombre}
                        {c.sin_conciliar > 0 && <span className="ml-2 text-xs text-amber-600">{c.sin_conciliar} sin conciliar</span>}
                      </td>
                      <td className={`p-3 text-right whitespace-nowrap tabular-nums ${claseMonto(c.saldo_inicial)}`}>{mc(c.saldo_inicial, c.moneda)}</td>
                      <td className="p-3 text-right whitespace-nowrap tabular-nums text-green-700">{mc(c.ingresos, c.moneda)}</td>
                      <td className="p-3 text-right whitespace-nowrap tabular-nums text-red-600">{mc(c.egresos, c.moneda)}</td>
                      <td className={`p-3 text-right whitespace-nowrap tabular-nums text-slate-500 ${claseMonto(c.transferencias)}`}>{mc(c.transferencias, c.moneda)}</td>
                      <td className={`p-3 text-right whitespace-nowrap tabular-nums font-semibold ${claseMonto(c.saldo_final)}`}>{mc(c.saldo_final, c.moneda)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  {datos.totales_cuentas.map((t) => (
                    <tr key={t.moneda} className="border-t bg-slate-50 font-semibold">
                      <td className="p-3">Total en {t.moneda === 'BS' ? 'Bs' : t.moneda}</td>
                      <td className="p-3 text-right whitespace-nowrap tabular-nums">{mc(t.saldo_inicial, t.moneda)}</td>
                      <td className="p-3 text-right whitespace-nowrap tabular-nums">{mc(t.ingresos, t.moneda)}</td>
                      <td className="p-3 text-right whitespace-nowrap tabular-nums">{mc(t.egresos, t.moneda)}</td>
                      <td className="p-3 text-right whitespace-nowrap tabular-nums">{mc(t.transferencias, t.moneda)}</td>
                      <td className="p-3 text-right whitespace-nowrap tabular-nums">{mc(t.saldo_final, t.moneda)}</td>
                    </tr>
                  ))}
                </tfoot>
              </table>
            </div>
          </Bloque>

          {/* Resultados */}
          <Bloque
            titulo="Resultados del período"
            enlace={`/reportes/estado-resultados?desde=${datos.desde}&hasta=${datos.hasta}`}
            textoEnlace="Ver estado de resultados"
          >
            <table className="w-full text-sm">
              <tbody>
                {ESTRUCTURA.map((e) => {
                  if (e.total) {
                    const v = datos.resultados[e.total].total;
                    return (
                      <tr key={e.total} className={`border-t ${e.final ? 'bg-slate-50 font-bold' : 'font-semibold'}`}>
                        <td className="p-3">{e.label}</td>
                        <td className={`p-3 text-right whitespace-nowrap tabular-nums ${claseMonto(v)}`}>{m(v)}</td>
                      </tr>
                    );
                  }
                  const s = sec[e.seccion];
                  if (!s || !s.lineas.length) return null;
                  const abierta = !!abiertas[s.key];
                  return (
                    <React.Fragment key={s.key}>
                      <tr className={`border-t cursor-pointer hover:bg-slate-50 ${FOCO_FILA}`} {...filaClickeable(() => setAbiertas((a) => ({ ...a, [s.key]: !a[s.key] })))}>
                        <td className="p-3">
                          <span className="inline-flex items-center gap-1">
                            {abierta ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            {s.label}
                            <span className="text-xs text-slate-400">({s.lineas.length})</span>
                          </span>
                        </td>
                        <td className={`p-3 text-right whitespace-nowrap tabular-nums ${claseMonto(s.total)}`}>{m(s.total)}</td>
                      </tr>
                      {abierta && s.lineas.map((l) => (
                        <tr key={l.clave} className="text-slate-500">
                          <td className="py-1.5 pl-10 pr-3">{l.grupo_nombre !== l.nombre ? `${l.grupo_nombre} · ` : ''}{l.nombre}</td>
                          <td className={`py-1.5 px-3 text-right whitespace-nowrap tabular-nums ${claseMonto(l.total)}`}>{m(l.total)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </Bloque>

          {/* Balance */}
          <Bloque
            titulo={`Balance al ${formatFecha(datos.balance.fecha)}`}
            enlace={`/reportes/balance-general?fecha=${datos.balance.fecha}`}
            textoEnlace="Ver balance general"
          >
            <div className="grid gap-px bg-slate-100 sm:grid-cols-3">
              {[['Activo', datos.balance.activo, datos.balance.total_activo], ['Pasivo', datos.balance.pasivo, datos.balance.total_pasivo], ['Patrimonio', datos.balance.patrimonio, datos.balance.total_patrimonio]].map(([label, bloques, total]) => (
                <div key={label} className="bg-white p-4 text-sm">
                  <div className="flex justify-between font-semibold text-slate-700">
                    <span>{label}</span>
                    <span className={`tabular-nums ${claseMonto(total)}`}>{m(total)}</span>
                  </div>
                  <ul className="mt-2 space-y-1 text-slate-500">
                    {bloques.filter((b) => Math.abs(b.total) >= 0.005).map((b) => (
                      <li key={b.key} className="flex justify-between gap-2">
                        <span>{b.label}</span>
                        <span className={`tabular-nums whitespace-nowrap ${claseMonto(b.total)}`}>{m(b.total)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Bloque>

          {/* Por cobrar y por pagar */}
          <div className="grid gap-6 lg:grid-cols-2">
            {[
              ['Por cobrar al cierre', cxc, '/reportes/antiguedad-por-cobrar', 'Cliente'],
              ['Por pagar al cierre', cxp, '/reportes/antiguedad-por-pagar', 'Proveedor']
            ].map(([titulo, b, ir, parte]) => (
              <Bloque key={titulo} titulo={titulo} enlace={ir} textoEnlace="Ver antigüedad (hoy)">
                {!b || b.lineas.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-slate-400">Nada pendiente a esa fecha.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-left">
                      <tr><th className="p-3">{parte}</th><th className="p-3 text-right">Saldo</th></tr>
                    </thead>
                    <tbody>
                      {[...b.lineas].sort((x, y) => y.monto - x.monto).slice(0, 10).map((l) => (
                        <tr key={l.nombre} className="border-t">
                          <td className="p-3">{l.nombre}</td>
                          <td className={`p-3 text-right whitespace-nowrap tabular-nums ${claseMonto(l.monto)}`}>{m(l.monto)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-slate-50 font-semibold">
                        <td className="p-3">Total{b.lineas.length > 10 ? ` (${b.lineas.length}, se muestran los 10 mayores)` : ''}</td>
                        <td className={`p-3 text-right whitespace-nowrap tabular-nums ${claseMonto(b.total)}`}>{m(b.total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </Bloque>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
